import { Button, ListItem, Menu, Stack } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import SettingsIcon from "@mui/icons-material/Settings";
import { selectVisibleTabularViewColumns } from "~/Search/redux/searchSelectors";
import { updateColumnVisibilityThunk } from "~/Search/components/ProductResults/views/NewTabularView/ConfigureColumnsButton/actions";
import type { RootState } from "~/redux/store";
import { ColumnConfigurator } from "~/Search/components/ProductResults/views/NewTabularView/ConfigureColumnsButton/ColumnConfigurator";
import { produce } from "immer";
import { useTranslation } from "react-i18next";

interface ConfigureColumnButtonProps {
  className?: string;
}

export const ConfigureColumnsButton = ({ className }: ConfigureColumnButtonProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const dispatch = useDispatch();

  const [columnState, setColumnState] = useState<
    { code: string; visible: boolean; label: string; originalPosition: number }[]
  >([]);
  const store = useStore();
  useEffect(() => {
    if (open) {
      const state = store.getState() as RootState;
      const columns = selectVisibleTabularViewColumns(state) ?? [];
      setColumnState(
        columns.map((c) => ({
          code: c.techValueUID,
          visible: c.visible,
          label: c.hoverTitle,
          originalPosition: c.originalPosition,
        })),
      );
    }
  }, [open]);

  const onReset = () => {
    const newVisibility = columnState.map((i) => ({ ...i, visible: true }));

    newVisibility.sort((a, b) => a.originalPosition - b.originalPosition);
    setColumnState(newVisibility);
    dispatch(updateColumnVisibilityThunk(newVisibility, true));
    setOpen(false);
  };

  const onSubmit = () => {
    setColumnState((prev) => ({ ...prev, visible: true }));
    dispatch(updateColumnVisibilityThunk(columnState));
    setOpen(false);
  };

  const moveColumn = useCallback((oldIndex: number, newIndex: number) => {
    setColumnState((prev) => {
      return produce(prev, (draft) => {
        const item = draft.splice(oldIndex, 1)[0];
        draft.splice(newIndex, 0, item);
      });
    });
  }, []);

  const updateVisibility = useCallback((index: number, visible: boolean) => {
    setColumnState((prev) => {
      return produce(prev, (draft) => {
        draft[index].visible = visible;
      });
    });
  }, []);

  return (
    <>
      <Button
        variant={"text"}
        className={className}
        onClick={() => setOpen(true)}
        ref={anchorRef}
        startIcon={<SettingsIcon />}
      >
        {t("page.srp.tabular.btn.manageColumns")}
      </Button>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorRef.current}
        slotProps={{ list: { sx: { gap: "8px", display: "flex", flexDirection: "column" } } }}
      >
        <ColumnConfigurator items={columnState} moveColumn={moveColumn} updateVisibility={updateVisibility} />

        <ListItem component={Stack} direction="row" spacing={2}>
          <Button fullWidth variant={"text"} onClick={onReset}>
            Reset
          </Button>
          <Button fullWidth onClick={onSubmit}>
            Save
          </Button>
        </ListItem>
      </Menu>
    </>
  );
};
